import React, { createContext, useContext, useEffect, useState } from 'react';
import { UserRoles } from '../types/users';
import { saveAuthSessionValue } from '../helpers/authSession';
import { LayoutType } from "../types/common/layout";
import { AuthType } from "../types/auth"

interface UserContextType {
    showMyClaims: boolean;
    setShowMy: (value: boolean) => void;
    notificationsEnabled: boolean | null;
    setNotificationsEnabled: (value: boolean) => void;
    showNewClaims: boolean;
    setShowNew: (value: boolean) => void;
    role: UserRoles | null;
    setRole: (role: UserRoles | null) => void;
    userUuid: string | null;
    setUserUuid: (uuid: string | null) => void;
    jwtToken: string | null;
    setJwtToken: (token: string | null) => void;
    jwtRefreshToken: string | null;
    setJwtRefreshToken: (token: string | null) => void;
    layout: LayoutType | null;
    setLayout: (layout: LayoutType | null) => void;
    userPinData: () => UserPinData | undefined;
    setUserPin: (userPin: string) => void;
    isEnteredPinDate: () => boolean;
    setLastEnteredUserPinDate: () => void;
    lastPageUrl: string | null;
    setLastPageUrl: (value: string | null) => void;
    userAuthType: AuthType | null;
    setUserAuthType: (type: AuthType | null) => void;
}

interface UserPinData {
    userUuid: string;
    userPin: string;
    lastEnteredDate: Date;
}

const UserContext = createContext<UserContextType>({
    // Уведомления
    notificationsEnabled: null,
    setNotificationsEnabled: () => {
    },
    // Для глобального фильтра
    showMyClaims: false,
    setShowMy: () => {
    },
    showNewClaims: false,
    setShowNew: () => {
    },
    // uuid профиля
    userUuid: null,
    setUserUuid: () => {
    },
    // Роль
    role: null,
    setRole: () => {
    },
    // JWT токен
    jwtToken: null,
    setJwtToken: () => {
    },
    // токен для обновления JWT
    jwtRefreshToken: null,
    setJwtRefreshToken: () => {
    },
    // Разметка
    layout: null,
    setLayout: () => {
    },
    // Пин пользователя
    userPinData: () => undefined,
    setUserPin: (userPin: string) => {
    },
    // Был ли введен пин
    isEnteredPinDate: () => false,
    setLastEnteredUserPinDate: () => {
    },
    // Страница на которую перешел пользователь до того как потребовался ввод кода
    lastPageUrl: null,
    setLastPageUrl: () => {
    },
    // Каким способом пользователь авторизовался
    userAuthType: null,
    setUserAuthType: () => {
    }
});
export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [role, setRole] = useState<UserRoles | null>(() => {
        const storedRole = localStorage.getItem('xUserRole');
        return storedRole ? JSON.parse(storedRole) : null;
    });
    const [jwtToken, setJwtToken] = useState<string | null>(() => {
        return localStorage.getItem('xAuthToken');
    });
    const [jwtRefreshToken, setJwtRefreshToken] = useState<string | null>(() => {
        return localStorage.getItem('xAuthRefreshToken');
    });
    const [layout, setLayout] = useState<LayoutType | null>(() => {
        const layout = localStorage.getItem('xLayout');
        if (layout === 'ADMIN') {
            return 'ADMIN'
        }
        if (layout === 'MANAGER') {
            return 'MANAGER'
        }
        return null;
    });
    const [userUuid, setUserUuid] = useState<string | null>(() => {
        return localStorage.getItem('xProfilePk');
    });
    const [showMyClaims, setShowMy] = useState<boolean>(() => {
        const showMy = localStorage.getItem('xShowMyClaims');
        if (!showMy) {
            return false;
        }
        return JSON.parse(showMy);
    });
    const [showNewClaims, setShowNew] = useState<boolean>(() => {
        const showNew = localStorage.getItem('xShowNewClaims');
        if (!showNew) {
            return false;
        }
        return JSON.parse(showNew);
    });
    const [notificationsEnabled, setNotificationsEnabled] = useState<boolean | null>(() => {
        const notificationsIsOn = localStorage.getItem('xNotificationsEnabled');
        if (notificationsIsOn == null) {
            return null;
        }
        if (!notificationsIsOn) {
            return false;
        }

        return JSON.parse(notificationsIsOn);
    });
    const [lastPageUrl, setLastPageUrl] = useState<string | null>(null);
    const [userAuthType, setUserAuthType] = useState<AuthType | null>(() => {
        const authType = localStorage.getItem('xUserAuthType');
        if (authType?.toUpperCase() === 'LOGIN') {
            return AuthType.LOGIN
        }
        if (authType?.toUpperCase() === 'PHONE') {
            return AuthType.PHONE
        }
        return null;
    });

    useEffect(() => {
        if (role) {
            saveAuthSessionValue('xUserRole', JSON.stringify(role));
        } else {
            localStorage.removeItem('xUserRole');
        }
    }, [role]);

    useEffect(() => {
        if (jwtToken) {
            saveAuthSessionValue('xAuthToken', jwtToken);
        } else {
            localStorage.removeItem('xAuthToken');
        }
    }, [jwtToken]);

    useEffect(() => {
        if (jwtRefreshToken) {
            saveAuthSessionValue('xAuthRefreshToken', jwtRefreshToken);
        } else {
            localStorage.removeItem('xAuthRefreshToken');
        }
    }, [jwtRefreshToken]);

    useEffect(() => {
        if (layout) {
            saveAuthSessionValue('xLayout', layout);
        } else {
            localStorage.removeItem('xLayout');
        }
    }, [layout]);

    useEffect(() => {
        if (userUuid) {
            saveAuthSessionValue('xProfilePk', userUuid);
        } else {
            localStorage.removeItem('xProfilePk');
        }
    }, [userUuid]);

    useEffect(() => {
        if (showMyClaims) {
            saveAuthSessionValue('xShowMyClaims', '' + showMyClaims);
        } else {
            localStorage.removeItem('xShowMyClaims');
        }
    }, [showMyClaims]);

    useEffect(() => {
        if (showNewClaims) {
            saveAuthSessionValue('xShowNewClaims', '' + showNewClaims);
        } else {
            localStorage.removeItem('xShowNewClaims');
        }
    }, [showNewClaims]);

    useEffect(() => {
        saveAuthSessionValue('xNotificationsEnabled', '' + notificationsEnabled);
    }, [notificationsEnabled]);

    useEffect(() => {
        if (userAuthType) {
            saveAuthSessionValue('xUserAuthType', userAuthType);
        } else {
            localStorage.removeItem('xUserAuthType');
        }
    }, [userAuthType]);

    // User Pin Date

    const getUsersPinData = (): UserPinData[] => {
        const usersData = localStorage.getItem('xUserPinData');
        return usersData ? JSON.parse(usersData) : [];
    };

    const setUserPinData = (data: UserPinData[]) => {
        saveAuthSessionValue('xUserPinData', JSON.stringify(data));
    };

    const setUserPin = (userPin: string) => {
        if (userUuid) {
            const parsedData = getUsersPinData();
            const userIndex = parsedData.findIndex(el => el.userUuid === userUuid);
            const lastEnteredDate = new Date();

            if (userIndex !== -1) {
                parsedData[userIndex].userPin = userPin;
            } else {
                parsedData.push({ userUuid, userPin, lastEnteredDate });
            }

            setUserPinData(parsedData);
        }
    };
    const userPinData = (): UserPinData | undefined => {
        const usersData = localStorage.getItem('xUserPinData');
        if (usersData) {
            const parsedData: UserPinData[] = JSON.parse(usersData);
            const userData = parsedData.find((el: UserPinData) => el.userUuid === userUuid);
            return userData
        }
        return undefined;
    };

    const removeUserPin = () => {
        if (userUuid) {
            const parsedData = getUsersPinData();
            const userIndex = parsedData.findIndex(el => el.userUuid === userUuid);

            if (userIndex !== -1) {
                parsedData.splice(userIndex, 1);
                setUserPinData(parsedData);
            }
        }
    };

    // User Pin Entered Status
    const isEnteredPinDate = (): boolean => {
        const pinData = userPinData();
        if (pinData && pinData.lastEnteredDate) {
            const lastEnteredDate = new Date(pinData.lastEnteredDate);
            if (!isNaN(lastEnteredDate.getTime())) {
                return !hasHoursPassed(lastEnteredDate, 24);
            }
            return false;
        }
        return false;
    };

    const setLastEnteredUserPinDate = () => {
        if (userUuid) {
            const parsedData = getUsersPinData();
            const userIndex = parsedData.findIndex(el => el.userUuid === userUuid);
            const lastEnteredDate = new Date();

            if (userIndex !== -1) {
                parsedData[userIndex].lastEnteredDate = lastEnteredDate;
                setUserPinData(parsedData);
            }
        }
    };

    const hasHoursPassed = (date: Date, hours: number): boolean => {
        const now = new Date();
        const timeDifference = now.getTime() - date.getTime();
        const hoursDifference = timeDifference / (1000 * 60 * 60);

        return hoursDifference >= hours;
    };

    return (
        <UserContext.Provider
            value={{
                role, setRole,
                jwtToken, setJwtToken,
                jwtRefreshToken, setJwtRefreshToken,
                layout, setLayout,
                showMyClaims, setShowMy,
                showNewClaims, setShowNew,
                notificationsEnabled, setNotificationsEnabled,
                userUuid, setUserUuid,
                userPinData, setUserPin,
                isEnteredPinDate, setLastEnteredUserPinDate,
                lastPageUrl, setLastPageUrl,
                userAuthType, setUserAuthType
            }}>
            {children}
        </UserContext.Provider>
    );
};

export const useUserContext = () => {
    const context = useContext(UserContext);
    if (!context) {
        throw new Error('Использование useUserContext должно быть внутри связанного с ним провайдера UserProvider');
    }
    return context;
};